import type { Feature, Polygon } from "geojson";
import type { BehaviorSubject } from "rxjs";
import type { FillExtrusionLayerSpecification, Map } from "@iventis/mapbox-gl";
import { DataField } from "@iventis/domain-model/model/dataField";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { ModelStyle } from "@iventis/domain-model/model/modelStyle";
import { MapState } from "../../types/store-schema";
import { MapboxLocalLayer } from "./mapbox-local-layer";
import { iventis3DAreaToBaseSublayer } from "../mapbox/engine-mapbox-helpers";
import { ModelStandStemLayer } from "../generic-layers/model-stand-stem-layer";
import { StylePropertyToValueMapV2 } from "../../types/style-types";

export class MapboxModelStandStemLayer extends ModelStandStemLayer {
    private readonly mapboxLayer: MapboxLocalLayer<Feature<Polygon, Record<string, number>>[], FillExtrusionLayerSpecification>;

    private readonly altitudeDataField: DataField;

    private readonly baseLayerId: string;

    private readonly map: Map;

    private readonly metadata: { type: "stand-stem"; name: string };

    constructor(state$: BehaviorSubject<MapState>, layerId: string, map: Map, metadata: { type: "stand-stem"; name: string }) {
        super(state$, layerId);
        this.metadata = metadata;
        this.baseLayerId = layerId;
        this.map = map;
        this.mapboxLayer = new MapboxLocalLayer(map, this.layerId, this.sourceId);
        this.altitudeDataField = this.state$.value.projectDataFields.find((pdf) => pdf.systemDataFieldName === SystemDataFieldName.ModelAltitude);
        this.addLocalSource();
        this.addLayer();
    }

    protected addLayer(): void {
        const mapboxLayer = iventis3DAreaToBaseSublayer(this.stemLayer, this.layerId, undefined);
        if (mapboxLayer.type === "fill-extrusion") {
            const iventisModelLayer = this.state$.value.layers.value.find((l) => l.id === this.baseLayerId);
            const altitudeStyleValue = getStaticStyleValue(iventisModelLayer.modelStyle.modelAltitude) ?? 0;
            mapboxLayer.paint["fill-extrusion-height"] = ["+", altitudeStyleValue, ["get", this.altitudeDataField.id]];
            // Ensures the stand layer works with terrain when altitude is less than 1.5m in height
            // https://github.com/mapbox/mapbox-gl-js/issues/13580
            mapboxLayer.paint["fill-extrusion-height-alignment"] = "terrain";
            mapboxLayer.metadata = this.metadata;
            this.mapboxLayer.addLayer(mapboxLayer, this.baseLayerId);
        }
    }

    protected addLocalSource(): void {
        this.mapboxLayer.addSource();
    }

    public updateLocalSource(source: Feature<Polygon>[]): void {
        this.mapboxLayer.updateSource(source);
    }

    public removeLayer(): void {
        this.mapboxLayer.removeLayer();
    }

    public removeLocalSource(): void {
        this.mapboxLayer.removeSource();
    }

    public updateStyle(styleChanges: StylePropertyToValueMapV2<ModelStyle>): void {
        if (styleChanges.modelAltitude) {
            const altitudeStyleValue = getStaticStyleValue(styleChanges.modelAltitude) ?? 0;
            this.map.setPaintProperty(this.layerId, "fill-extrusion-height", ["+", altitudeStyleValue, ["coalesce", ["get", this.altitudeDataField.id], 0]]);
        }
    }

    protected hideLayer(): void {
        this.mapboxLayer.hideLayer();
    }

    protected showLayer(): void {
        this.mapboxLayer.showLayer();
    }

    public updateMapOrder(): void {
        this.mapboxLayer.updateMapOrder(this.baseLayerId);
    }

    protected updateMapLevel(): void {
        throw new Error("Method not implemented.");
    }

    public getLayerIdsForSnapping(): string[] {
        throw new Error("Method not implemented.");
    }
}
